import { useEffect as useEffectM, useRef as useRefM, useState as useStateM } from "react";
import { Api } from "./api";
import { Banner, BylawRow, Field, Icon, Sparkline, Stat, TrendChart } from "./components";

// Manager experience: community dashboard, bylaws upload + versions, roster.

function _fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function _fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/* ─── Household count editor ─────────────────────────────────────────────── */
// Inline editor for community.unit_count. The count drives per-household
// billing (PR 5+), the polish-pool quota (PR 2+), and the PMC portfolio
// totals (PR 5b+).
//
// PR 6 routing:
// - INCREASE or first-time set → apply immediately + push new quantity
//   to Stripe (one-way revenue ratchet, no approval needed)
// - DECREASE → server creates a UnitCountChangeRequest (status: pending)
//   and returns 202; this UI shows the pending state with a "cancel
//   request" affordance, and the operator approves/denies it from the
//   admin queue.
function UnitCountEditor({ value, onSaved }) {
  const [editing, setEditing] = useStateM(false);
  const [draft, setDraft] = useStateM(value == null ? "" : String(value));
  const [reason, setReason] = useStateM("");
  const [busy, setBusy] = useStateM(false);
  const [error, setError] = useStateM(null);
  const [pendingRequest, setPendingRequest] = useStateM(null);

  // On mount + after every save, refresh whether there's a pending
  // decrease request waiting on operator approval. Renders different
  // copy if so.
  //
  // Defensive note: the backend returns JSON `null` when there's no
  // pending request, but apiFetch normalizes falsy bodies to `{}`
  // (`return data || {}` in api.jsx) so we receive a truthy object
  // either way. Check for a real request id rather than truthiness —
  // otherwise every manager would see the pending-approval banner
  // with all fields undefined. (Codex P1 catch on PR #100.)
  const loadPending = () => {
    Api.adminCommunityPendingUnitCountChange()
      .then((p) => setPendingRequest(p && p.id ? p : null))
      .catch(() => { /* non-critical */ });
  };
  useEffectM(() => { loadPending(); }, []);

  const parsed = draft.trim() === "" ? null : Number(draft);
  const isValid = parsed === null || (Number.isInteger(parsed) && parsed > 0 && parsed <= 100000);
  const isDecrease = value != null && parsed != null && parsed < value;
  const isIncrease = value == null || (parsed != null && parsed > value);

  const save = async () => {
    setError(null);
    if (!isValid) {
      setError("Enter a whole number between 1 and 100,000.");
      return;
    }
    if (isDecrease && !reason.trim()) {
      setError("Add a short reason — operators need to know why before approving a decrease.");
      return;
    }
    setBusy(true);
    try {
      const updated = await Api.adminCommunityUpdate({
        unit_count: parsed,
        unit_count_reason: isDecrease ? reason.trim() : undefined,
      });
      // Server returns 202 when it created a pending request. We
      // can't tell from the parsed body alone (no status code at
      // this layer), so we refresh the pending state — if a request
      // was created we'll see it.
      onSaved(updated.unit_count);
      setEditing(false);
      setReason("");
      await loadPending();
    } catch (err) {
      setError(err.message || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  const cancelPending = async () => {
    if (!window.confirm("Cancel this pending decrease request? You can submit a new one any time.")) return;
    setBusy(true);
    try {
      await Api.adminCommunityCancelPendingUnitCountChange();
      setPendingRequest(null);
    } catch (err) {
      setError(err.message || "Couldn't cancel.");
    } finally {
      setBusy(false);
    }
  };

  // Pending-request banner takes priority over the editor — the value
  // is locked while a request is in flight (you can't double-submit a
  // decrease while one's already waiting).
  if (pendingRequest) {
    return (
      <div className="text-sm" style={{
        marginTop: 8, padding: 10, borderRadius: 8,
        background: "var(--accent-soft, #eef3e6)",
        color: "var(--accent-ink, #3d4f2a)",
      }}>
        <div>
          <b>{_fmtNumM(value)} households</b>
          {" — pending decrease to "}
          <b>{_fmtNumM(pendingRequest.requested_count)}</b>
          {", awaiting operator approval."}
        </div>
        {pendingRequest.reason && (
          <div className="text-xs" style={{ marginTop: 4, opacity: 0.85 }}>
            Reason: {pendingRequest.reason}
          </div>
        )}
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={cancelPending}
          disabled={busy}
          style={{ marginTop: 8 }}
        >
          {busy ? "Cancelling…" : "Cancel request"}
        </button>
      </div>
    );
  }

  if (!editing) {
    return (
      <div className="text-sm" style={{ marginTop: 8 }}>
        {value == null ? (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => { setDraft(""); setReason(""); setEditing(true); }}
          >
            Set household count
          </button>
        ) : (
          <span style={{ color: "var(--mute)" }}>
            <b style={{ color: "var(--ink)" }}>{_fmtNumM(value)}</b> households ·{" "}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setDraft(String(value)); setReason(""); setEditing(true); }}
            >Edit</a>
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="col" style={{ gap: 8, marginTop: 8 }}>
      <div className="row" style={{ gap: 8, alignItems: "center" }}>
        <input
          className="input"
          type="number"
          min="1"
          max="100000"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. 120"
          style={{ width: 120 }}
          autoFocus
        />
        <button type="button" className="btn btn--primary btn--sm" onClick={save} disabled={busy}>
          {busy ? "Saving…" : (isDecrease ? "Submit for approval" : "Save")}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setEditing(false); setReason(""); setError(null); }} disabled={busy}>
          Cancel
        </button>
      </div>
      {isIncrease && value != null && (
        <div className="text-xs text-mute">
          Going from {_fmtNumM(value)} → <b>{_fmtNumM(parsed)}</b>. Applies immediately and updates your billing on the next invoice.
        </div>
      )}
      {isDecrease && (
        <>
          <div className="text-xs" style={{ color: "var(--accent-ink)" }}>
            Decreases need operator approval. Going from {_fmtNumM(value)} → <b>{_fmtNumM(parsed)}</b> will create a pending request.
          </div>
          <input
            className="input"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why? e.g. 'sold 10 units, now 200 doors'"
            maxLength={500}
          />
        </>
      )}
      {error && <span className="text-xs" style={{ color: "var(--danger)" }}>{error}</span>}
    </div>
  );
}

/* ─── Dashboard ──────────────────────────────────────────────────────────── */
const TIER_PILL_M = {
  trial: "pill--ghost",
  starter: "pill--green",
  pro: "pill--gold",
  enterprise: "pill--accent",
};
const tierLabelM = (t) => t ? (t[0].toUpperCase() + t.slice(1)) : "—";
const _fmtNumM = (n) => (n || 0).toLocaleString();

function ManagerDashboard({ onRoute }) {
  const [c, setC] = useStateM({ loading: true, error: null, data: null });
  const [usersState, setUsersState] = useStateM({ loading: true, error: null, users: [] });

  useEffectM(() => {
    let cancelled = false;
    Api.adminCommunity()
      .then((data) => { if (!cancelled) setC({ loading: false, error: null, data }); })
      .catch((err) => { if (!cancelled) setC({ loading: false, error: err.message || "Couldn't load community.", data: null }); });
    Api.adminCommunityUsers()
      .then((users) => { if (!cancelled) setUsersState({ loading: false, error: null, users: users || [] }); })
      .catch((err) => { if (!cancelled) setUsersState({ loading: false, error: err.message || "Couldn't load roster.", users: [] }); });
    return () => { cancelled = true; };
  }, []);

  if (c.loading) {
    return <div className="scroll"><div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>Loading dashboard…</div></div>;
  }
  if (c.error) {
    return <div className="scroll"><div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{c.error}</div></div>;
  }
  const data = c.data;
  const homeowners = (usersState.users || []).filter((u) => u.role === "homeowner");
  const managers = (usersState.users || []).filter((u) => u.role === "manager" || u.role === "company_admin" || u.role === "super_admin");

  return (
    <div className="scroll">
      <div className="stack-lg">
        {/* Header summary */}
        <div className="card card--feature card--pad-lg">
          <div className="row" style={{ alignItems: "flex-start", justifyContent: "space-between", gap: 24 }}>
            <div>
              <div className="row" style={{ gap: 8, marginBottom: 10 }}>
                <span className={`pill ${TIER_PILL_M[data.subscription_tier] || "pill--ghost"}`}>
                  <span className="pill__dot" /> {tierLabelM(data.subscription_tier)} tier
                </span>
                <span className="pill pill--ghost text-mono">{data.slug}</span>
              </div>
              <h2 className="display" style={{ fontSize: 36, fontWeight: 400, letterSpacing: "-0.02em" }}>{data.name}</h2>
              <p className="text-mute" style={{ marginTop: 6, maxWidth: 540 }}>
                {_fmtNumM(data.user_count)} {data.user_count === 1 ? "member" : "members"} ·{" "}
                {_fmtNumM(data.active_bylaw_count)} active {data.active_bylaw_count === 1 ? "bylaw" : "bylaws"}
              </p>
              <UnitCountEditor
                value={data.unit_count}
                onSaved={(next) => setC((s) => ({ ...s, data: { ...s.data, unit_count: next } }))}
              />
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn--ghost" onClick={() => onRoute && onRoute("bylaws-mgmt")}>
                <Icon name="upload" size={15} /> Upload bylaw
              </button>
              <button className="btn btn--primary" onClick={() => onRoute && onRoute("roster")}>
                <Icon name="users" size={15} /> View roster
              </button>
            </div>
          </div>

          {/* Note: token / cost / budget meter intentionally NOT shown
              here. That's operator-only data — managers don't pay for
              tokens (Compass absorbs that as part of the per-household
              subscription), so surfacing "Estimated cost so far $0.00"
              both confused the meaning of "cost" and leaked operator
              margin data. The full token + cost meter lives in the
              Operator overview (admin.jsx). Managers see Questions
              asked + member counts in the Stats row below instead. */}
        </div>

        {/* Stats row */}
        <div className="grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          <Stat label="Questions asked (all-time)" value={_fmtNumM(data.totals.questions)} />
          <Stat label="Homeowners" value={usersState.loading ? "…" : _fmtNumM(homeowners.length)} />
          <Stat label="Managers" value={usersState.loading ? "…" : _fmtNumM(managers.length)} />
          <Stat label="Active bylaws" value={_fmtNumM(data.active_bylaw_count)} />
        </div>

        <PendingEventProposals />

        <TopBylawCitations />

        <TownSquareCategoryEditor />

        <div className="card card--pad-lg" style={{ color: "var(--mute)" }}>
          <b style={{ color: "var(--ink)" }}>Coming later:</b> flagged-for-review queue and request-handling latency. Those need analytics tables the backend doesn't compute yet.
        </div>
      </div>
    </div>
  );
}


/* ─── Pending event proposals (manager-only dashboard widget) ────────── */
// Homeowner proposals from the calendar land here. Click a row to
// expand the description + location; Approve flips status to APPROVED
// and fires the community-wide event_posted fan-out, Deny is silent
// to the community but bells the proposer.
//
// Default OPEN when there are pending items (so action items don't
// hide), default CLOSED when the queue is empty.

function PendingEventProposals() {
  const [state, setState] = useStateM({ loading: true, error: null, list: [] });
  const [expandedId, setExpandedId] = useStateM(null);
  const [acting, setActing] = useStateM(null); // event id currently being moderated

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    Api.eventsPending()
      .then((list) => setState({ loading: false, error: null, list: list || [] }))
      .catch((err) => setState({
        loading: false,
        error: err.message || "Couldn't load pending proposals.",
        list: [],
      }));
  };
  useEffectM(() => { load(); }, []);

  const moderate = (event, action) => {
    if (acting) return;
    setActing(event.id);
    Api.eventModerate(event.id, action)
      .then(() => { setExpandedId(null); load(); })
      .catch((err) => setState((s) => ({
        ...s, error: err.message || `Couldn't ${action} proposal.`,
      })))
      .finally(() => setActing(null));
  };

  const count = state.list.length;
  const fmtWhen = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  };

  return (
    <details className="card card--pad-lg" open={count > 0}>
      <summary
        className="sc-manage__summary"
        style={{ cursor: "pointer", listStyle: "none" }}
      >
        <Icon name="calendar" size={16} />{" "}
        <b style={{ color: "var(--ink)" }}>Pending event proposals</b>
        {count > 0 && (
          <span className="sc-manage__badge" style={{ marginLeft: 8 }}>{count}</span>
        )}
        <span className="text-sm text-mute" style={{ marginLeft: 8 }}>
          Submitted by homeowners — approve to publish to the calendar
        </span>
      </summary>

      <div style={{ marginTop: 12 }}>
        {state.loading && (
          <div className="text-sm text-mute">Loading…</div>
        )}
        {state.error && (
          <div className="text-sm" style={{ color: "var(--danger)" }}>{state.error}</div>
        )}
        {!state.loading && !state.error && state.list.length === 0 && (
          <div className="text-sm text-mute">
            No pending proposals. When a homeowner suggests an event from
            the calendar page, it shows up here.
          </div>
        )}
        {!state.loading && !state.error && state.list.length > 0 && (
          <div className="sc-roster">
            {state.list.map((e) => {
              const expanded = expandedId === e.id;
              const busy = acting === e.id;
              return (
                <div key={e.id} className="sc-roster__row" style={{ flexDirection: "column", alignItems: "stretch" }}>
                  <div
                    className="row"
                    style={{ gap: 10, alignItems: "center", width: "100%", cursor: "pointer" }}
                    onClick={() => setExpandedId(expanded ? null : e.id)}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: "var(--ink)" }}>{e.title}</div>
                      <div className="text-sm text-mute">
                        {fmtWhen(e.starts_at)}{e.location ? ` · ${e.location}` : ""}
                      </div>
                      <div className="text-sm text-mute">
                        Proposed by {e.created_by_name || "a resident"}
                      </div>
                    </div>
                    <Icon
                      name="chevron"
                      size={14}
                      style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform .12s" }}
                    />
                  </div>
                  {expanded && (
                    <div style={{ marginTop: 10 }}>
                      {e.description && (
                        <div
                          className="text-sm"
                          style={{ whiteSpace: "pre-wrap", color: "var(--ink-2)", marginBottom: 10 }}
                        >
                          {e.description}
                        </div>
                      )}
                      <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => moderate(e, "deny")}
                          disabled={busy}
                        >
                          {busy ? "…" : "Deny"}
                        </button>
                        <button
                          className="btn btn--primary btn--sm"
                          onClick={() => moderate(e, "approve")}
                          disabled={busy}
                        >
                          {busy ? "Approving…" : "Approve"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </details>
  );
}


/* ─── Top cited bylaw sections (manager-only dashboard widget) ───────── */
// Privacy-preserving "what are residents concerned about" signal:
// counts UNIQUE USERS per (document, heading) over the last 30 days,
// top 10 buckets. No query text is exposed, only the citation
// rollup. Default open so it's the first thing a manager sees.

function TopBylawCitations() {
  const [state, setState] = useStateM({ loading: true, error: null, data: null });

  useEffectM(() => {
    let cancelled = false;
    Api.adminCommunityTopBylawCitations()
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: null, data });
      })
      .catch((err) => {
        if (!cancelled) setState({
          loading: false,
          error: err.message || "Couldn't load top citations.",
          data: null,
        });
      });
    return () => { cancelled = true; };
  }, []);

  const window = state.data?.window_days || 30;
  const results = state.data?.results || [];

  return (
    <details className="card card--pad-lg" open>
      <summary className="sc-manage__summary" style={{ cursor: "pointer", listStyle: "none" }}>
        <Icon name="trend" size={16} />{" "}
        <b style={{ color: "var(--ink)" }}>Top bylaw topics</b>
        <span className="text-sm text-mute" style={{ marginLeft: 8 }}>
          Most-cited sections in the last {window} days · privacy-safe (no query text)
        </span>
      </summary>

      <div style={{ marginTop: 12 }}>
        {state.loading && (
          <div className="text-sm text-mute">Loading…</div>
        )}
        {state.error && (
          <div className="text-sm" style={{ color: "var(--danger)" }}>{state.error}</div>
        )}
        {!state.loading && !state.error && results.length === 0 && (
          <div className="text-sm text-mute">
            No citations in the last {window} days yet. Once residents start
            using Consult Compass, the bylaw sections they ask about will
            roll up here.
          </div>
        )}
        {!state.loading && !state.error && results.length > 0 && (
          <ol className="sc-roster" style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {results.map((r, i) => (
              <li
                key={`${r.bylaw_document_id}-${r.heading || "_no_heading"}`}
                className="sc-roster__row"
              >
                <span
                  className="pill"
                  style={{ minWidth: 26, justifyContent: "center" }}
                >
                  {i + 1}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="sc-roster__name">{r.bylaw_title}</div>
                  {r.heading && (
                    <div className="text-sm text-mute">{r.heading}</div>
                  )}
                </div>
                <div className="text-sm" style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 600, color: "var(--ink)" }}>
                    {r.unique_users}
                  </div>
                  <div className="text-mute">
                    {r.unique_users === 1 ? "resident" : "residents"}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </details>
  );
}


/* ─── Town Square categories (manager-only editor) ───────────────────────── */
// Add + hide only in MVP — rename / reorder are follow-ups. The
// resident-facing tab strip in Town Square pulls only is_active=true;
// hidden categories stay readable via their topics but disappear from
// the new-topic dropdown.
function TownSquareCategoryEditor() {
  const [state, setState] = useStateM({ loading: true, error: null, cats: [] });
  const [draft, setDraft] = useStateM("");
  const [adding, setAdding] = useStateM(false);
  const [addError, setAddError] = useStateM(null);
  // Confirm-delete modal: { cat } when open, null when closed.
  const [confirm, setConfirm] = useStateM(null);

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    Api.townSquareCategories()
      .then((cats) => setState({ loading: false, error: null, cats: cats || [] }))
      .catch((err) => setState({ loading: false, error: err.message || "Couldn't load categories.", cats: [] }));
  };
  useEffectM(() => { load(); }, []);

  const add = () => {
    const name = draft.trim();
    if (!name || adding) return;
    setAdding(true); setAddError(null);
    Api.townSquareCreateCategory({ name })
      .then((c) => {
        setDraft("");
        setState((s) => ({ ...s, cats: [...s.cats, c] }));
      })
      .catch((err) => setAddError(err.message || "Couldn't add category."))
      .finally(() => setAdding(false));
  };

  const toggle = (cat) => {
    const next = !cat.is_active;
    // Optimistic flip so the UI feels instant; revert on failure.
    setState((s) => ({
      ...s,
      cats: s.cats.map((c) => c.id === cat.id ? { ...c, is_active: next } : c),
    }));
    Api.townSquareSetCategoryActive(cat.id, next).catch(() => {
      setState((s) => ({
        ...s,
        cats: s.cats.map((c) => c.id === cat.id ? { ...c, is_active: cat.is_active } : c),
      }));
    });
  };

  const performDelete = (cat) => Api.townSquareDeleteCategory(cat.id)
    .then((res) => {
      setState((s) => ({ ...s, cats: s.cats.filter((c) => c.id !== cat.id) }));
      setConfirm(null);
      return res;
    });

  return (
    <>
      <details className="card card--pad-lg ts-mgr__details">
        <summary className="ts-mgr__summary">
          <h3 className="display ts-mgr__title">Town Square categories</h3>
          <span className="ts-mgr__summary-meta">
            {state.loading ? "…" : `${state.cats.length} categories`}
            <Icon name="chevronDown" size={14} className="ts-mgr__chevron" />
          </span>
        </summary>

        <p className="text-sm text-mute" style={{ margin: "4px 0 12px" }}>
          Add a new category, hide one residents don't need, or delete one
          (topics in a deleted category move to <b>General</b>).
        </p>

        {state.loading ? (
          <div style={{ color: "var(--mute)" }}>Loading…</div>
        ) : state.error ? (
          <div style={{ color: "var(--danger)" }}>{state.error}</div>
        ) : (
          <>
            <div className="ts-mgr__list">
              {state.cats.map((c) => {
                const isGeneral = c.slug === "general";
                return (
                  <div key={c.id} className={`ts-mgr__row ${c.is_active ? "" : "ts-mgr__row--hidden"}`}>
                    <div className="ts-mgr__name">
                      {c.name}
                      {!c.is_active && <span className="ts-mgr__hidden-tag">hidden</span>}
                      <span className="text-mono text-xs" style={{ color: "var(--mute-2)", marginLeft: 8 }}>{c.slug}</span>
                    </div>
                    <div className="row" style={{ gap: 6 }}>
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => toggle(c)}
                        title={c.is_active ? "Hide from residents" : "Show to residents"}
                      >
                        {c.is_active ? "Hide" : "Show"}
                      </button>
                      {!isGeneral && (
                        <button
                          className="btn btn--ghost btn--sm ts-mgr__delete-btn"
                          onClick={() => setConfirm({ cat: c })}
                          title="Delete this category — topics will move to General"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="ts-mgr__add">
              <input
                type="text"
                className="input"
                placeholder="New category name (e.g. Pool & Spa Rules)"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") add(); }}
                maxLength={120}
                disabled={adding}
              />
              <button className="btn btn--primary" onClick={add} disabled={!draft.trim() || adding}>
                {adding ? "Adding…" : "Add category"}
              </button>
            </div>
            {addError && <div className="text-sm" style={{ color: "var(--danger)", marginTop: 6 }}>{addError}</div>}
          </>
        )}
      </details>

      {confirm && (
        <ConfirmDeleteCategoryModal
          cat={confirm.cat}
          onCancel={() => setConfirm(null)}
          onConfirm={performDelete}
        />
      )}
    </>
  );
}

/* Confirm-delete modal: blocks the destructive write until the manager
 * types "DELETE" exactly. Mike's spec: irreversible delete + topic
 * reassignment to General should require typed confirmation, not just a
 * single tap.
 */
function ConfirmDeleteCategoryModal({ cat, onCancel, onConfirm }) {
  const [typed, setTyped] = useStateM("");
  const [submitting, setSubmitting] = useStateM(false);
  const [error, setError] = useStateM(null);
  const armed = typed === "DELETE";

  const go = () => {
    if (!armed || submitting) return;
    setSubmitting(true); setError(null);
    onConfirm(cat).catch((err) => {
      setError(err.message || "Delete failed.");
      setSubmitting(false);
    });
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="ts-del-title">
      <div className="modal" style={{ maxWidth: 480 }}>
        <h3 id="ts-del-title" className="display" style={{ fontSize: 20, fontWeight: 500, marginBottom: 8 }}>
          Delete category &ldquo;{cat.name}&rdquo;?
        </h3>
        <p style={{ color: "var(--mute)", fontSize: 14, lineHeight: 1.5 }}>
          Any topics currently filed under <b>{cat.name}</b> will be moved to <b>General</b>.
          This can't be undone.
        </p>
        <p style={{ color: "var(--mute)", fontSize: 14, marginTop: 12 }}>
          Type <b className="text-mono" style={{ color: "var(--danger)" }}>DELETE</b> to confirm.
        </p>
        <input
          type="text"
          className="input"
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && armed) go(); }}
          placeholder="DELETE"
          aria-label="Type DELETE to confirm"
          style={{ marginTop: 6, fontFamily: "var(--font-mono)" }}
          disabled={submitting}
        />
        {error && <div className="text-sm" style={{ color: "var(--danger)", marginTop: 8 }}>{error}</div>}
        <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button className="btn btn--ghost" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button
            className="btn btn--primary"
            onClick={go}
            disabled={!armed || submitting}
            style={armed ? { background: "var(--danger, #c0392b)", borderColor: "var(--danger, #c0392b)" } : {}}
          >
            {submitting ? "Deleting…" : "Delete category"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ─── Bylaws management (with upload + version history) ──────────────────── */
function ManagerBylaws() {
  const [showUpload, setShowUpload] = useStateM(false);
  const [replaceTitle, setReplaceTitle] = useStateM("");  // when "Replace" is clicked
  const [historyOf, setHistoryOf] = useStateM(null);  // {id, title}
  const [query, setQuery] = useStateM("");
  const [showArchived, setShowArchived] = useStateM(false);
  const [reloadKey, setReloadKey] = useStateM(0);
  const [list, setList] = useStateM({ loading: true, error: null, docs: [] });
  const [rowPendingId, setRowPendingId] = useStateM(null);
  const [rowError, setRowError] = useStateM(null);  // { id, msg }

  useEffectM(() => {
    let cancelled = false;
    setList({ loading: true, error: null, docs: [] });
    Api.bylawsList({ include_inactive: showArchived })
      .then((docs) => { if (!cancelled) setList({ loading: false, error: null, docs: docs || [] }); })
      .catch((err) => { if (!cancelled) setList({ loading: false, error: err.message || "Couldn't load bylaws.", docs: [] }); });
    return () => { cancelled = true; };
  }, [reloadKey, showArchived]);

  // Poll bylaws list while any doc is mid-OCR (#371). OCR runs in a
  // background thread on the server; the list refresh swaps the
  // "Processing OCR…" pill out as soon as the status flips to indexed
  // (or ocr_failed). Stops automatically once nothing is pending so we
  // don't burn cycles on a quiet tab.
  const hasOcrPending = (list.docs || []).some(
    (d) => d.chunk_index_status === "ocr_pending" || d.chunk_index_status === "ocr_processing"
  );
  useEffectM(() => {
    if (!hasOcrPending) return undefined;
    const t = setInterval(() => setReloadKey((k) => k + 1), 8000);
    return () => clearInterval(t);
  }, [hasOcrPending]);

  const visible = (() => {
    if (!query) return list.docs;
    const q = query.toLowerCase();
    return list.docs.filter((d) =>
      (d.title || "").toLowerCase().includes(q) || (d.description || "").toLowerCase().includes(q)
    );
  })();

  const archivedCount = list.docs.filter((d) => d.is_active === false).length;

  const openUpload = (titleToReplace = "") => {
    setReplaceTitle(titleToReplace);
    setShowUpload(true);
  };
  const closeUpload = () => {
    setShowUpload(false);
    setReplaceTitle("");
  };
  // Called by the upload card right after a successful API call. Reloads
  // the list so the new doc appears in the background while the user
  // reads the status message in the still-open modal.
  const onUploaded = () => {
    setReloadKey((k) => k + 1);
  };

  const deactivate = async (d) => {
    if (!window.confirm(`Archive "${d.title}"? Compass will stop using it immediately. Reversible.`)) return;
    setRowPendingId(d.id);
    setRowError(null);
    try {
      await Api.bylawsDeactivate(d.id);
      // Reflect in place: if archived view is hidden, drop the row; else mark inactive.
      setList((s) => ({
        ...s,
        docs: showArchived
          ? s.docs.map((x) => (x.id === d.id ? { ...x, is_active: false } : x))
          : s.docs.filter((x) => x.id !== d.id),
      }));
    } catch (err) {
      setRowError({ id: d.id, msg: err.message || "Couldn't archive." });
    } finally {
      setRowPendingId(null);
    }
  };

  const reactivate = async (d) => {
    setRowPendingId(d.id);
    setRowError(null);
    try {
      const res = await Api.bylawsReactivate(d.id);
      setList((s) => ({
        ...s,
        docs: s.docs.map((x) => (x.id === d.id ? { ...x, is_active: res.is_active } : x)),
      }));
    } catch (err) {
      setRowError({ id: d.id, msg: err.message || "Couldn't reactivate." });
    } finally {
      setRowPendingId(null);
    }
  };

  // Per-row permanent-delete state: which id has the typed-confirm UI open
  // and what they've typed so far. Only archived rows can be hard-deleted.
  const [permDeleteId, setPermDeleteId] = useStateM(null);
  const [permDeleteInput, setPermDeleteInput] = useStateM("");

  // Per-row rename state — same pattern as permanent-delete (one
  // bylaw inline-editable at a time). Issue #233.
  const [renameId, setRenameId] = useStateM(null);
  const [renameInput, setRenameInput] = useStateM("");

  const openRename = (d) => {
    setRenameId(d.id);
    setRenameInput(d.title || "");
    setRowError(null);
  };
  const closeRename = () => {
    setRenameId(null);
    setRenameInput("");
  };
  const submitRename = async (d) => {
    const next = renameInput.trim();
    if (!next || next === d.title) { closeRename(); return; }
    setRowPendingId(d.id);
    setRowError(null);
    try {
      const res = await Api.bylawsRename(d.id, next);
      setList((s) => ({
        ...s,
        docs: s.docs.map((x) => (x.id === d.id ? { ...x, title: res.title } : x)),
      }));
      closeRename();
    } catch (err) {
      setRowError({ id: d.id, msg: err.message || "Couldn't rename." });
    } finally {
      setRowPendingId(null);
    }
  };

  const openPermDelete = (d) => {
    setPermDeleteId(d.id);
    setPermDeleteInput("");
    setRowError(null);
  };
  const closePermDelete = () => {
    setPermDeleteId(null);
    setPermDeleteInput("");
  };
  const permanentlyDelete = async (d) => {
    if (permDeleteInput !== "DELETE") return;
    setRowPendingId(d.id);
    setRowError(null);
    try {
      await Api.bylawsPermanentlyDelete(d.id);
      setList((s) => ({ ...s, docs: s.docs.filter((x) => x.id !== d.id) }));
      setPermDeleteId(null);
      setPermDeleteInput("");
    } catch (err) {
      setRowError({ id: d.id, msg: err.message || "Couldn't delete permanently." });
    } finally {
      setRowPendingId(null);
    }
  };

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 920 }}>
        <Banner icon="shield">
          <b>Bylaws are the source of truth.</b> When you upload a revision, every
          answer Compass gives going forward uses the new version. Archived ones
          are skipped by Compass but kept on file so historical citations stay valid.
        </Banner>

        <div className="row" style={{ marginBottom: 6 }}>
          <div className="card" style={{ padding: "8px 12px", flex: 1, display: "flex", alignItems: "center", gap: 10 }}>
            <Icon name="search" size={16} style={{ color: "var(--mute)" }} />
            <input
              className="input"
              aria-label="Search bylaw documents"
              placeholder="Search documents…"
              style={{ border: 0, padding: 0, background: "transparent" }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <label className="row" style={{ gap: 6, padding: "0 6px", fontSize: 13, color: "var(--mute)", cursor: "pointer" }}>
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Show archived{showArchived && archivedCount ? ` (${archivedCount})` : ""}
          </label>
          <button className="btn btn--accent" onClick={() => openUpload("")}><Icon name="upload" size={16} /> Upload new bylaw</button>
        </div>

        {showUpload && (
          <UploadCard
            initialTitle={replaceTitle}
            onClose={closeUpload}
            onUploaded={onUploaded}
          />
        )}

        {!historyOf && (
          <>
            {list.loading && <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>Loading bylaws…</div>}
            {list.error && <div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{list.error}</div>}
            {!list.loading && !list.error && visible.length === 0 && (
              <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
                {list.docs.length === 0
                  ? "No bylaws uploaded yet. Upload your first document to get Compass answering questions."
                  : `No matches for "${query}".`}
              </div>
            )}
            {visible.length > 0 && (
              <div className="stack-md">
                {visible.map((d) => {
                  const archived = d.is_active === false;
                  const isConfirmingDelete = permDeleteId === d.id;
                  return (
                    <div key={d.id} className="bylaw" style={{ opacity: archived ? 0.55 : 1, alignItems: "flex-start" }}>
                      <div className="bylaw__ico" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {renameId === d.id ? (
                          <div className="row" style={{ gap: 6, alignItems: "center", marginBottom: 4 }}>
                            <input
                              className="input"
                              value={renameInput}
                              onChange={(e) => setRenameInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); submitRename(d); }
                                if (e.key === "Escape") { e.preventDefault(); closeRename(); }
                              }}
                              autoFocus
                              maxLength={200}
                              aria-label="Bylaw title"
                              disabled={rowPendingId === d.id}
                              style={{ flex: 1 }}
                            />
                            <button
                              className="btn btn--sm btn--primary"
                              onClick={() => submitRename(d)}
                              disabled={!renameInput.trim() || renameInput.trim() === d.title || rowPendingId === d.id}
                            >
                              {rowPendingId === d.id ? "Saving…" : "Save"}
                            </button>
                            <button
                              className="btn btn--sm btn--ghost"
                              onClick={closeRename}
                              disabled={rowPendingId === d.id}
                            >Cancel</button>
                          </div>
                        ) : (
                          <div className="bylaw__title">{d.title}</div>
                        )}
                        <div className="bylaw__meta">
                          {archived
                            ? <span className="pill pill--ghost">Archived</span>
                            : <span className="pill pill--green"><span className="pill__dot" /> Active</span>}
                          {(d.chunk_index_status === "ocr_pending" || d.chunk_index_status === "ocr_processing") && (
                            <>
                              <span style={{ margin: "0 8px", color: "var(--hairline-2)" }}>·</span>
                              <span
                                className="pill"
                                title={d.chunk_index_status === "ocr_processing"
                                  ? "OCR running on the server right now. This row becomes searchable as soon as it finishes."
                                  : "Image-only PDF. Waiting in the OCR queue."}
                                style={{ background: "var(--accent-soft)", color: "var(--accent-ink)" }}
                              >
                                <span className="pill__dot" style={{ animation: "pulse 1.6s ease-in-out infinite" }} />
                                {" "}{d.chunk_index_status === "ocr_processing" ? "Processing OCR…" : "Queued for OCR"}
                              </span>
                            </>
                          )}
                          {d.chunk_index_status === "ocr_failed" && (
                            <>
                              <span style={{ margin: "0 8px", color: "var(--hairline-2)" }}>·</span>
                              <span
                                className="pill"
                                title="OCR couldn't read this PDF. Re-upload to retry, or contact support."
                                style={{ background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)" }}
                              >
                                OCR failed
                              </span>
                            </>
                          )}
                          <span style={{ margin: "0 8px", color: "var(--hairline-2)" }}>·</span>
                          <span className="mono">v{d.version}</span>
                          <span style={{ margin: "0 8px", color: "var(--hairline-2)" }}>·</span>
                          <span className="mono">{_fmtBytes(d.size_bytes)}</span>
                          <span style={{ margin: "0 8px", color: "var(--hairline-2)" }}>·</span>
                          Uploaded {_fmtDate(d.uploaded_at)}
                        </div>
                        {d.description && (
                          <div className="text-sm text-mute" style={{ marginTop: 4 }}>{d.description}</div>
                        )}
                        {rowError && rowError.id === d.id && (
                          <div className="text-xs" style={{ color: "var(--danger)", marginTop: 4 }}>{rowError.msg}</div>
                        )}
                        {isConfirmingDelete && (
                          <div style={{
                            marginTop: 10, padding: "10px 12px", borderRadius: 8,
                            background: "var(--danger-soft, #fdecea)",
                            border: "1px solid var(--danger, #b3261e)",
                            color: "var(--danger, #b3261e)",
                          }}>
                            <div style={{ fontSize: 13, marginBottom: 8 }}>
                              <b>This permanently removes "{d.title}" and its Claude file.</b> Irreversible — there's no undo. Type <span className="mono" style={{ color: "var(--ink)" }}>DELETE</span> to confirm.
                            </div>
                            <div className="row" style={{ gap: 8, alignItems: "center" }}>
                              <input
                                className="input mono"
                                placeholder="DELETE"
                                value={permDeleteInput}
                                onChange={(e) => setPermDeleteInput(e.target.value)}
                                autoFocus
                                style={{ flex: 1, maxWidth: 180 }}
                                disabled={rowPendingId === d.id}
                              />
                              <button
                                className="btn btn--sm btn--ghost"
                                onClick={closePermDelete}
                                disabled={rowPendingId === d.id}
                              >Cancel</button>
                              <button
                                className="btn btn--sm"
                                onClick={() => permanentlyDelete(d)}
                                disabled={permDeleteInput !== "DELETE" || rowPendingId === d.id}
                                style={{
                                  background: "var(--danger)", color: "#fff",
                                  opacity: permDeleteInput === "DELETE" ? 1 : 0.5,
                                }}
                              >
                                {rowPendingId === d.id ? "Deleting…" : "Permanently delete"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="row" style={{ gap: 8 }}>
                        <button className="btn btn--sm btn--ghost" onClick={() => setHistoryOf({ id: d.id, title: d.title })}>
                          <Icon name="history" size={14} /> Versions
                        </button>
                        {!archived && (
                          <>
                            <button
                              className="btn btn--sm btn--ghost"
                              onClick={() => openRename(d)}
                              disabled={rowPendingId === d.id || renameId === d.id}
                            >
                              <Icon name="edit" size={14} /> Rename
                            </button>
                            <button className="btn btn--sm btn--ghost" onClick={() => openUpload(d.title)}>
                              <Icon name="upload" size={14} /> Replace
                            </button>
                            <button
                              className="btn btn--sm btn--ghost"
                              onClick={() => deactivate(d)}
                              disabled={rowPendingId === d.id}
                              style={{ color: "var(--danger)" }}
                            >
                              {rowPendingId === d.id ? "Archiving…" : "Archive"}
                            </button>
                          </>
                        )}
                        {archived && (
                          <>
                            <button
                              className="btn btn--sm btn--ghost"
                              onClick={() => reactivate(d)}
                              disabled={rowPendingId === d.id || isConfirmingDelete}
                              style={{ color: "var(--accent-ink)" }}
                            >
                              {rowPendingId === d.id && !isConfirmingDelete ? "Restoring…" : "Reactivate"}
                            </button>
                            {!isConfirmingDelete && (
                              <button
                                className="btn btn--sm btn--ghost"
                                onClick={() => openPermDelete(d)}
                                disabled={rowPendingId === d.id}
                                style={{ color: "var(--danger)" }}
                              >
                                Delete permanently
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {historyOf && <VersionHistory doc={historyOf} onBack={() => setHistoryOf(null)} />}
      </div>
    </div>
  );
}

// Claude's chat document blocks only accept PDF and plaintext. We
// could let DOCX through to the Files API, but the moment a chat tries
// to use it Anthropic returns 400. Keep the allow-list aligned with
// what actually answers questions.
const ALLOWED_EXTS = ["pdf", "txt", "md"];
const MAX_BYTES = 32 * 1024 * 1024;

function UploadCard({ initialTitle = "", onClose, onUploaded }) {
  const fileInputRef = useRefM(null);
  const [file, setFile] = useStateM(null);
  const [title, setTitle] = useStateM(initialTitle);
  const [description, setDescription] = useStateM("");
  const [busy, setBusy] = useStateM(false);
  const [status, setStatus] = useStateM(null);   // { kind: "error"|"ok", msg }
  const [dragOver, setDragOver] = useStateM(false);
  // Once we've completed at least one upload, swap "Cancel" -> "Done"
  // and disable the Upload button so the modal becomes a "read the
  // result" state. The user dismisses on their own time.
  const [completed, setCompleted] = useStateM(false);

  const pickFile = () => fileInputRef.current?.click();

  const acceptFile = (f) => {
    setStatus(null);
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      setStatus({ kind: "error", msg: `Unsupported file type ".${ext}". Allowed: ${ALLOWED_EXTS.join(", ")}.` });
      setFile(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setStatus({ kind: "error", msg: `That file is ${_fmtBytes(f.size)}. The limit is ${_fmtBytes(MAX_BYTES)}.` });
      setFile(null);
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) acceptFile(f);
  };

  const submit = async () => {
    setStatus(null);
    if (!file) { setStatus({ kind: "error", msg: "Pick a file first." }); return; }
    if (!title.trim()) { setStatus({ kind: "error", msg: "Title can't be empty." }); return; }
    setBusy(true);
    try {
      const data = await Api.bylawsUpload({ file, title: title.trim(), description: description.trim() });
      if (data.status === "unchanged") {
        // Backend now returns a more informative message when extraction was
        // attempted but the PDF has no usable text layer. Fall back to the
        // historical wording otherwise.
        setStatus({
          kind: "ok",
          msg: data.message || "Already up to date — content matches your current active version.",
        });
      } else if (data.converted_to_markdown && data.original_size_bytes) {
        const shrink = data.original_size_bytes / Math.max(1, data.size_bytes);
        setStatus({
          kind: "ok",
          msg: `Uploaded as v${data.version}. PDF auto-converted to Markdown — ${_fmtBytes(data.original_size_bytes)} → ${_fmtBytes(data.size_bytes)} (${shrink.toFixed(1)}× smaller).`,
        });
      } else {
        setStatus({ kind: "ok", msg: `Uploaded as v${data.version}.` });
      }
      // Trigger list refresh in the background but leave the modal open
      // so the user can read the status message. They dismiss with Done.
      setCompleted(true);
      if (onUploaded) onUploaded();
    } catch (err) {
      setStatus({ kind: "error", msg: err.message || "Upload failed." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card card--pad-lg" style={{ background: "var(--surface-2)", borderColor: "var(--accent)", borderStyle: "dashed" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 14 }}>
        <h3 className="display" style={{ fontSize: 22, fontWeight: 500 }}>
          {initialTitle ? `Replace "${initialTitle}"` : "Upload a bylaw"}
        </h3>
        <button className="btn btn--quiet btn--sm" onClick={onClose} disabled={busy}>
          {completed ? "Done" : "Cancel"}
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        accept=".pdf,.txt,.md,application/pdf,text/plain,text/markdown"
        onChange={(e) => acceptFile(e.target.files?.[0])}
      />

      <div
        onClick={pickFile}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          cursor: "pointer",
          border: `1.5px dashed ${dragOver ? "var(--accent-ink)" : "var(--accent)"}`,
          borderRadius: 12,
          padding: "32px 24px",
          textAlign: "center",
          background: dragOver ? "var(--accent-soft)" : "var(--accent-soft)",
          color: "var(--accent-ink)",
          opacity: busy ? 0.6 : 1,
        }}
      >
        <Icon name="upload" size={28} />
        <div className="display" style={{ fontSize: 20, fontWeight: 400, marginTop: 10 }}>
          {file ? file.name : "Drop a PDF, TXT, or MD here"}
        </div>
        <div className="text-sm" style={{ marginTop: 6, opacity: 0.8 }}>
          {file
            ? `${_fmtBytes(file.size)} · click to choose a different file`
            : `Or click to browse. Max ${_fmtBytes(MAX_BYTES)} per document.`}
        </div>
        <div className="text-xs text-mono" style={{ marginTop: 12, opacity: 0.65 }}>
          We hash the contents — identical to your current version means no-op, no re-upload.
        </div>
      </div>

      <div className="col" style={{ gap: 12, marginTop: 16 }}>
        <Field label="Title" hint="Used to match against an existing bylaw — same title = new version of that document.">
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy}
            placeholder="e.g. CC&Rs"
          />
        </Field>
        <Field label="What changed in this version?" hint="Optional. Shown to managers in the version list.">
          <textarea
            className="textarea"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy}
            placeholder="Section 8.3 expanded — short-term rental cap revised…"
          />
        </Field>
      </div>

      {status && (
        <div role={status.kind === "error" ? "alert" : "status"} style={{
          marginTop: 12, padding: "10px 12px", borderRadius: 8, fontSize: 14,
          background: status.kind === "error" ? "var(--danger-soft, #fdecea)" : "var(--accent-soft, #eef7f1)",
          color: status.kind === "error" ? "var(--danger, #b3261e)" : "var(--accent-ink, #1f4634)",
          border: `1px solid ${status.kind === "error" ? "var(--danger, #b3261e)" : "var(--accent, #2d5d4f)"}`,
        }}>
          {status.msg}
        </div>
      )}

      <div className="row" style={{ gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
        <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
          {completed ? "Done" : "Cancel"}
        </button>
        <button className="btn btn--accent" onClick={submit} disabled={busy || !file || completed}>
          {busy ? "Uploading…" : (initialTitle ? "Publish new version" : "Upload")}
        </button>
      </div>
    </div>
  );
}

function VersionHistory({ doc, onBack }) {
  const [state, setState] = useStateM({ loading: true, error: null, versions: [] });

  useEffectM(() => {
    let cancelled = false;
    Api.bylawsHistory(doc.id)
      .then((versions) => { if (!cancelled) setState({ loading: false, error: null, versions: versions || [] }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message || "Couldn't load history.", versions: [] }); });
    return () => { cancelled = true; };
  }, [doc.id]);

  const { loading, error, versions } = state;

  return (
    <div className="card card--pad-lg">
      <button className="btn btn--ghost btn--sm" onClick={onBack} style={{ marginBottom: 16 }}>
        <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> Back
      </button>
      <h3 className="display" style={{ fontSize: 24, fontWeight: 400 }}>{doc.title}</h3>
      <p className="text-mute text-sm" style={{ marginTop: 4 }}>
        {loading ? "Loading…" : `Version history · ${versions.length} ${versions.length === 1 ? "version" : "versions"}`}
      </p>
      {error && <div style={{ color: "var(--danger)", marginTop: 12 }}>{error}</div>}
      <hr className="divider" />
      <div className="col" style={{ gap: 0 }}>
        {versions.map((v, i) => (
          <div key={v.id} style={{ display: "grid", gridTemplateColumns: "48px 1fr auto", gap: 16, padding: "16px 0", borderTop: i === 0 ? 0 : "1px dashed var(--hairline)" }}>
            <span style={{
              width: 38, height: 38, borderRadius: "50%",
              background: v.is_active ? "var(--accent-soft)" : "var(--bg-2)",
              color: v.is_active ? "var(--accent-ink)" : "var(--mute)",
              display: "grid", placeItems: "center",
              fontFamily: "var(--font-mono)", fontWeight: 600, fontSize: 13,
            }}>v{v.version}</span>
            <div>
              <div className="row" style={{ gap: 8 }}>
                {v.is_active
                  ? <span className="pill pill--green"><span className="pill__dot" /> Active</span>
                  : <span className="pill pill--ghost">Superseded {v.superseded_at ? `· ${_fmtDate(v.superseded_at)}` : ""}</span>
                }
              </div>
              <div className="text-sm text-mute mono" style={{ marginTop: 4 }}>
                Created {_fmtDate(v.created_at)} · sha256 {v.content_hash ? v.content_hash.slice(0, 12) + "…" : "—"}
              </div>
            </div>
            <div />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Roster ─────────────────────────────────────────────────────────────── */
const ROLE_LABEL = {
  super_admin: "Super admin",
  company_admin: "Company admin",
  manager: "Manager",
  homeowner: "Homeowner",
};
const ROLE_PILL = {
  super_admin: "pill--accent",
  company_admin: "pill--accent",
  manager: "pill--accent",
  homeowner: "pill--ghost",
};
const ROLE_AVATAR = {
  super_admin: "b",
  company_admin: "b",
  manager: "t",
  homeowner: "g",
};

function _initials(u) {
  const src = (u.full_name || u.email || "").trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function _relTime(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 30) return `${day} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function ManagerRoster({ user: me, onUserUpdate }) {
  const [filter, setFilter] = useStateM("all");
  const [state, setState] = useStateM({ loading: true, error: null, users: [] });
  // Per-row state for in-flight role changes and error messages.
  const [pendingId, setPendingId] = useStateM(null);
  const [rowError, setRowError] = useStateM(null);  // { id, msg }

  useEffectM(() => {
    let cancelled = false;
    Api.adminCommunityUsers()
      .then((users) => { if (!cancelled) setState({ loading: false, error: null, users: users || [] }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message || "Couldn't load roster.", users: [] }); });
    return () => { cancelled = true; };
  }, []);

  const { loading, error, users } = state;
  const homeowners = users.filter((u) => u.role === "homeowner");
  const managers = users.filter((u) => u.role !== "homeowner");
  const visible = filter === "all" ? users : filter === "homeowner" ? homeowners : managers;

  // A manager can promote a homeowner to manager or demote a manager to
  // homeowner within the same community. Editing a super_admin /
  // company_admin is intentionally hidden — that's an operator-level
  // change. Editing your own row is also hidden so you can't lock
  // yourself out.
  const canEditRole = (u) => {
    if (!me || u.id === me.id) return false;
    return u.role === "homeowner" || u.role === "manager";
  };

  const changeRole = async (u, nextRole) => {
    if (nextRole === u.role) return;
    setPendingId(u.id);
    setRowError(null);
    try {
      const updated = await Api.adminSetUserRole(u.id, nextRole);
      setState((s) => ({
        ...s,
        users: s.users.map((x) => (x.id === u.id ? { ...x, role: updated.role } : x)),
      }));
    } catch (err) {
      setRowError({ id: u.id, msg: err.message || "Couldn't change role." });
    } finally {
      setPendingId(null);
    }
  };

  // "Suspend AI" pauses this member's access to Consult Compass +
  // Project Builder polish in THIS community only. Per-membership,
  // doesn't touch the user's global account or AI access in other
  // HOAs they belong to. Managers can't toggle operators / company
  // admins; can't toggle themselves.
  const canSuspendAi = (u) => {
    if (!me || u.id === me.id || !u.is_active) return false;
    if (u.role === "super_admin" || u.role === "company_admin") return false;
    return true;
  };
  // Remove-from-community is gentler — the account survives, just
  // loses access to this HOA. Same rules as canDeactivate EXCEPT
  // an operator (super_admin) IS allowed to remove themselves —
  // unless this is their last super_admin membership, in which case
  // the backend rejects with self_lockout to avoid the next login
  // demoting them out of the operator console (Codex PR #106 P2
  // follow-up; mirrors admin.jsx:canRemove).
  const canSelfRemoveAsOperator = () => {
    if (!me || me.role !== "super_admin") return false;
    // The /me payload (auth/routes.py:_user_payload) tags each
    // membership with is_active_community and exposes the active
    // community id only as `me.community.id` — there's no top-level
    // `me.community_id` to compare against (Codex PR #110 P2 catch).
    // Use is_active_community as the key instead.
    const memberships = me.memberships || [];
    const here = memberships.find((m) => m.is_active_community);
    if (!here || here.role !== "super_admin") return true;
    return memberships.some(
      (m) => !m.is_active_community && m.role === "super_admin",
    );
  };
  const canRemove = (u) => {
    if (!me || !u.is_active) return false;
    if (u.role === "super_admin" || u.role === "company_admin") {
      // Only super_admin can prune another operator/admin, and only
      // self-prune is allowed at the manager level (don't let a
      // community manager evict an operator who happens to be a
      // member of their HOA).
      return u.id === me.id && canSelfRemoveAsOperator();
    }
    if (u.id === me.id) {
      // Self-remove for non-operator self is blocked — a community
      // manager removing themselves would orphan the community.
      return canSelfRemoveAsOperator();
    }
    return true;
  };
  const toggleAiSuspended = async (u) => {
    const nextSuspended = !u.ai_suspended;
    const prompt = nextSuspended
      ? `Suspend Consult Compass + Project Builder for ${u.full_name || u.email} in this community? They'll keep every other feature (Town Square, messages, marketplace, assistance). Their AI access in any other HOA is unaffected. Reversible with one click.`
      : `Restore AI access for ${u.full_name || u.email} in this community?`;
    if (!window.confirm(prompt)) return;
    setPendingId(u.id);
    setRowError(null);
    try {
      const res = await Api.managerSetMemberAiSuspended(u.id, nextSuspended);
      setState((s) => ({
        ...s,
        users: s.users.map((x) =>
          x.id === u.id ? { ...x, ai_suspended: !!res.ai_suspended } : x
        ),
      }));
    } catch (err) {
      setRowError({
        id: u.id,
        msg: err.message || (nextSuspended ? "Couldn't suspend AI." : "Couldn't restore AI."),
      });
    } finally {
      setPendingId(null);
    }
  };

  const removeFromCommunity = async (u) => {
    const isSelf = u.id === me?.id;
    const prompt = isSelf
      ? `Remove yourself from this community? Your operator account stays intact — you can re-add yourself any time from the admin endpoints.`
      : `Remove ${u.full_name || u.email} from this community? Their account stays intact — they just lose access to this HOA.`;
    if (!window.confirm(prompt)) return;
    setPendingId(u.id);
    setRowError(null);
    try {
      await Api.managerRemoveMembership(u.id);
      setState((s) => ({ ...s, users: s.users.filter((x) => x.id !== u.id) }));
      if (isSelf) {
        // Backend just re-anchored our active community to a fallback
        // membership. Refresh /me so the sidebar + dashboard reflect
        // the new active community immediately rather than showing
        // stale data from the community we just left.
        try {
          const fresh = await Api.me();
          if (onUserUpdate) onUserUpdate(fresh);
        } catch (_) { /* non-critical */ }
      }
    } catch (err) {
      setRowError({ id: u.id, msg: err.message || "Couldn't remove." });
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="scroll">
      <div className="stack-md">
        <Banner icon="users">
          <b>Privacy reminder.</b> You can see <i>how many</i> questions a homeowner
          has asked, but never their content. That's by design.
        </Banner>

        <div className="row" style={{ justifyContent: "space-between" }}>
          <div className="row top__role" style={{ background: "var(--surface)", border: "1px solid var(--hairline)" }}>
            {[
              ["all", `All · ${users.length}`],
              ["homeowner", `Homeowners · ${homeowners.length}`],
              ["manager", `Managers · ${managers.length}`],
            ].map(([k, l]) => (
              <button key={k} className={filter === k ? "is-active" : ""} onClick={() => setFilter(k)}>{l}</button>
            ))}
          </div>
        </div>

        {loading && <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>Loading roster…</div>}
        {error && <div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{error}</div>}
        {!loading && !error && users.length === 0 && (
          <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
            No members yet. Homeowners can self-serve sign up with your community slug.
          </div>
        )}
        {!loading && !error && visible.length === 0 && users.length > 0 && (
          <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>No members in this filter.</div>
        )}
        {visible.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last sign-in</th>
                  <th style={{ textAlign: "right" }}>Questions</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((u) => (
                  <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.55 }}>
                    <td>
                      <div className="row">
                        <span className={`avatar avatar--${ROLE_AVATAR[u.role] || "g"}`}>{_initials(u)}</span>
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {u.full_name || "(no name)"}
                            {me && u.id === me.id && <span className="text-xs text-mute mono" style={{ marginLeft: 6 }}>· you</span>}
                          </div>
                          <div className="text-sm text-mute mono">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {canEditRole(u) ? (
                        <div className="col" style={{ gap: 4 }}>
                          <select
                            className="select"
                            value={u.role}
                            onChange={(e) => changeRole(u, e.target.value)}
                            disabled={pendingId === u.id}
                            style={{ minWidth: 130, padding: "4px 8px", fontSize: 13 }}
                          >
                            <option value="homeowner">Homeowner</option>
                            <option value="manager">Manager</option>
                          </select>
                          {pendingId === u.id && <span className="text-xs text-mute">Saving…</span>}
                          {rowError && rowError.id === u.id && (
                            <span className="text-xs" style={{ color: "var(--danger)" }}>{rowError.msg}</span>
                          )}
                        </div>
                      ) : (
                        <span className={`pill ${ROLE_PILL[u.role] || "pill--ghost"}`}>{ROLE_LABEL[u.role] || u.role}</span>
                      )}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                        {u.is_active
                          ? <span className="pill pill--green"><span className="pill__dot" /> Active</span>
                          : <span className="pill pill--ghost">Deactivated</span>
                        }
                        {u.ai_suspended && (
                          <span
                            className="pill pill--ghost"
                            title="AI features (Consult Compass + Project Builder) are paused for this member in this community."
                            style={{ color: "var(--danger)" }}
                          >
                            AI suspended
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-sm" style={{ color: "var(--mute)" }}>{_relTime(u.last_login_at)}</td>
                    <td className="text-sm mono" style={{ textAlign: "right", color: "var(--mute)" }}>
                      {/* Tokens + cost columns intentionally removed —
                          managers don't pay for tokens and cost is
                          operator-side margin data. "Questions" is the
                          resident-facing analog: how engaged is this
                          person with Compass chat? */}
                      {_fmtNumM(u.usage?.questions_asked || 0)}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {canRemove(u) && (
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => removeFromCommunity(u)}
                          disabled={pendingId === u.id}
                          title={
                            u.id === me?.id
                              ? "Remove yourself from this community (your operator account stays intact)"
                              : "Remove from this community (account stays intact)"
                          }
                          style={{ marginRight: 4 }}
                        >
                          {pendingId === u.id ? "…" : (u.id === me?.id ? "Remove me" : "Remove")}
                        </button>
                      )}
                      {canSuspendAi(u) && (
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => toggleAiSuspended(u)}
                          disabled={pendingId === u.id}
                          style={{ color: u.ai_suspended ? undefined : "var(--danger)" }}
                          title={
                            u.ai_suspended
                              ? "Restore AI access (Consult Compass + Project Builder) for this member"
                              : "Pause AI access (Consult Compass + Project Builder) for this member in this community"
                          }
                        >
                          {pendingId === u.id ? "…" : (u.ai_suspended ? "Restore AI" : "Suspend AI")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export { ManagerDashboard, ManagerBylaws, ManagerRoster };
